/**
 * Sprint P32 · Cockpit Saved Views panel — persistent, deep-linkable.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FloppyDisk, LinkSimple, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, DialogHeader,
} from "@/components/ui/dialog";
import {
  listSavedViews, createSavedView, updateSavedView, deleteSavedView,
} from "@/lib/api";


export function SavedViewsPanel({ pid, currentState, onApply }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["saved-views", pid],
    queryFn: () => listSavedViews(pid),
    enabled: !!pid,
    staleTime: 30_000,
  });
  const [nameOpen, setNameOpen] = useState(false);
  const [name, setName] = useState("");
  const [renameOpen, setRenameOpen] = useState(null);
  const [renameVal, setRenameVal]   = useState("");

  const createMut = useMutation({
    mutationFn: (body) => createSavedView(pid, body),
    onSuccess: () => { toast.success("View saved"); setName(""); setNameOpen(false); qc.invalidateQueries({ queryKey: ["saved-views", pid] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => updateSavedView(pid, id, body),
    onSuccess: () => { toast.success("View updated"); setRenameOpen(null); qc.invalidateQueries({ queryKey: ["saved-views", pid] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => deleteSavedView(pid, id),
    onSuccess: () => { toast.success("View deleted"); qc.invalidateQueries({ queryKey: ["saved-views", pid] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const saveNew = () => {
    if (!name.trim()) return toast.error("Enter a view name");
    const snapshot = currentState();
    createMut.mutate({ name: name.trim(), ...snapshot });
  };
  const overwrite = (v) => {
    const snapshot = currentState();
    updateMut.mutate({ id: v.id, body: snapshot });
    toast.success(`"${v.name}" refreshed with current view`);
  };
  const duplicate = (v) => {
    createMut.mutate({ ...v, name: `${v.name} (copy)`, id: undefined });
  };
  const copyDeep = (v) => {
    const url = `${window.location.origin}${window.location.pathname}${v.deep_link || ""}`;
    navigator.clipboard.writeText(url);
    toast.success("Deep link copied");
  };

  const views = q.data?.views || [];
  return (
    <>
      <div className="px-3 py-2 border-t border-slate-800 flex items-center justify-between">
        <div className="text-[9px] font-mono uppercase tracking-widest text-sky-400">
          Saved views
        </div>
        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] bg-slate-900 text-slate-200 border-slate-700 hover:bg-slate-800"
                onClick={() => setNameOpen(true)}
                data-testid="cockpit-saved-view-new">
          <FloppyDisk size={10} className="mr-1" /> Save
        </Button>
      </div>
      <div className="px-2 pb-2 max-h-52 overflow-y-auto space-y-1" data-testid="cockpit-saved-views-list">
        {views.length === 0 && (
          <div className="text-[10px] text-slate-500 px-1 py-2 italic">
            No saved views yet.
          </div>
        )}
        {views.map((v) => (
          <div key={v.id}
               className="rounded border border-slate-800 bg-slate-900 p-2 hover:bg-slate-800"
               data-testid={`cockpit-saved-view-${v.id}`}>
            <div className="flex items-center justify-between gap-1">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-slate-100 truncate"
                     data-testid={`cockpit-saved-view-name-${v.id}`}>
                  {v.name}
                </div>
                <div className="text-[9px] text-slate-400 font-mono truncate">
                  {v.palette} · cutoff {v.cutoff ?? "—"} · {v.slicer !== "off" ? v.slicer.toUpperCase() : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1.5">
              <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] bg-slate-950 text-slate-100 border-slate-700 hover:bg-slate-800"
                      onClick={() => onApply(v)}
                      data-testid={`cockpit-saved-view-open-${v.id}`}>Open</Button>
              <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] bg-slate-950 text-slate-100 border-slate-700 hover:bg-slate-800"
                      onClick={() => overwrite(v)}
                      title="Save current view state on top of this saved view"
                      data-testid={`cockpit-saved-view-update-${v.id}`}>Update</Button>
              <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] bg-slate-950 text-slate-100 border-slate-700 hover:bg-slate-800"
                      onClick={() => copyDeep(v)}
                      data-testid={`cockpit-saved-view-link-${v.id}`}><LinkSimple size={9} /></Button>
              <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] bg-slate-950 text-slate-100 border-slate-700 hover:bg-slate-800"
                      onClick={() => duplicate(v)}
                      data-testid={`cockpit-saved-view-dup-${v.id}`}>Dup</Button>
              <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] bg-slate-950 text-slate-100 border-slate-700 hover:bg-slate-800"
                      onClick={() => { setRenameOpen(v); setRenameVal(v.name); }}
                      data-testid={`cockpit-saved-view-rename-${v.id}`}>Rn</Button>
              <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px] text-rose-400 border-rose-800 bg-slate-950 hover:bg-rose-900/40"
                      onClick={() => deleteMut.mutate(v.id)}
                      data-testid={`cockpit-saved-view-delete-${v.id}`}><Trash size={9} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={nameOpen} onOpenChange={setNameOpen}>
        <DialogContent data-testid="cockpit-saved-view-dialog">
          <DialogHeader>
            <DialogTitle>Save current view</DialogTitle>
            <DialogDescription>
              Camera, palette, layers, cutoff and slicer settings will be preserved.
            </DialogDescription>
          </DialogHeader>
          <Input placeholder="View name (e.g. 'NE section, Au cutoff 0.5')"
                 value={name} onChange={(e) => setName(e.target.value)}
                 className="h-9 text-[13px]"
                 data-testid="cockpit-saved-view-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNameOpen(false)}>Cancel</Button>
            <Button onClick={saveNew} disabled={createMut.isPending}
                    className="bg-brand-blue text-white"
                    data-testid="cockpit-saved-view-confirm">
              {createMut.isPending ? "Saving…" : "Save view"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameOpen} onOpenChange={(v) => !v && setRenameOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename view</DialogTitle></DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                 className="h-9 text-[13px]"
                 data-testid="cockpit-saved-view-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(null)}>Cancel</Button>
            <Button onClick={() => updateMut.mutate({ id: renameOpen.id, body: { name: renameVal } })}
                    className="bg-brand-blue text-white"
                    data-testid="cockpit-saved-view-rename-confirm">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
